import { Component, StrictMode, type ErrorInfo, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { initFontPreference } from './components/FontPicker';
import { initFrontendSentry, reportError } from './lib/sentry';
import { holdPictureUpClass, isPictureUpPage } from './lib/pu-class';
import { PU_ICONS } from './components/PictureUpBrand';
import './styles.css';
// PictureUp tokens, inert unless a PictureUp page sets html.pu.
import './pictureup.css';

// Error monitoring: off until the server hands out a DSN (no-op otherwise).
void initFrontendSentry();

/** Last-resort fallback, so a render crash shows a way out instead of a
 *  blank page, and gets reported. */
class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  private heldPu = false;
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(error: unknown, info: ErrorInfo) {
    reportError(error, { componentStack: info.componentStack });
  }
  override componentDidUpdate() {
    // The crash unmounted the app, and with it the PictureUp page that
    // held html.pu and the tab icon (their cleanups put the CRM's back).
    // On a PictureUp address put PictureUp's back; a share page gets
    // the neutral mark, as it does before it knows whose it is.
    if (!this.state.failed) return;
    const path = window.location.pathname;
    if (!isPictureUpPage(path)) return;
    const share = path.startsWith('/aroll/s/');
    // Hold the class like a PictureUp page does (a count, so the
    // crashed pages' own cleanups, which run later, cannot drop it).
    if (!share && !this.heldPu) {
      this.heldPu = true;
      holdPictureUpClass();
    }
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (icon) {
      icon.setAttribute('href', share ? '/brand/share-neutral.svg' : PU_ICONS.favicon);
      icon.setAttribute('type', 'image/svg+xml');
    }
    document.title = share ? 'Review' : 'PictureUp';
  }
  override render() {
    if (!this.state.failed) return this.props.children;
    // PictureUp (and its share pages) get the ink ground and a pill;
    // the CRM keeps the fallback it always had.
    const pu = isPictureUpPage(window.location.pathname);
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: pu ? '#0a0a0a' : '#0b0f17', color: pu ? '#f4f4f2' : '#e8ecf4', fontFamily: pu ? "'Geist', system-ui, sans-serif" : 'system-ui, sans-serif', padding: 24, textAlign: 'center' }}>
        <div>
          <p style={{ fontSize: pu ? 15 : 18, marginBottom: pu ? 20 : 12 }}>Something went wrong on this page.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={
              pu
                ? { padding: '9px 20px', borderRadius: 9999, border: 'none', background: '#f4f4f2', color: '#0a0a0a', fontFamily: 'inherit', fontWeight: 500, fontSize: 13, cursor: 'pointer' }
                : { padding: '10px 18px', borderRadius: 8, border: '1px solid #3a4560', background: 'transparent', color: 'inherit', cursor: 'pointer' }
            }
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

// Self-heal stale shells. After a deploy, a session still holding the
// old index.html requests lazy chunks whose hashes no longer exist on
// the server; Vite surfaces those failures as 'vite:preloadError'.
// Reload once to pick up the fresh shell. The timestamp guard stops a
// reload loop if the failure is something other than a deploy (at most
// one auto-reload per 30 seconds).
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'kg.chunk-reload-at';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 30_000) return;
  try {
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    // Private mode: still reload, just without loop protection.
  }
  event.preventDefault();
  window.location.reload();
});

initFontPreference();

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root element');

createRoot(root).render(
  <StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </StrictMode>,
);
